import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  TextField,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle,
  Download,
  CreditCard,
  Description,
  DataObject,
  Security,
  AccessTime,
  Business,
  TrendingUp,
  Public,
  Star,
  Email,
  Send,
  Visibility,
  ExpandMore,
  ExpandLess,
  TableChart
} from '@mui/icons-material';
import { API_URL } from './constants';
import axios from 'axios';

const AnugaDataView = () => {
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    dataSource: '',
    requirements: ''
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState(null);

  // Data preview state
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPage, setPreviewPage] = useState(0);
  const [previewRowsPerPage, setPreviewRowsPerPage] = useState(5);
  const [selectedDataset, setSelectedDataset] = useState('exhibitors');
  const [datasetsInfo, setDatasetsInfo] = useState(null);

  // Check if user already has access to the dataset
  useEffect(() => {
    checkAccessStatus();
    fetchDatasetsInfo();
    
    // Check for payment success from Stripe Payment Link redirect
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentIntentId = urlParams.get('payment_intent');
    
    if (sessionId) {
      // User returned from Stripe Payment Link checkout (using checkout session)
      verifyCheckoutSession(sessionId);
      
      // Clean up URL by removing query parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (paymentIntentId) {
      // User returned from Stripe Payment Link (using payment intent directly)
      // We can verify payment intent and grant access
      verifyPaymentIntent(paymentIntentId);
      
      // Clean up URL by removing query parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  const verifyCheckoutSession = async (sessionId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_URL}/data/anuga/verify-checkout`, {
        params: { session_id: sessionId }
      });
      
      if (response.data.success) {
        setPaymentStatus('success');
        setHasAccess(true);
        // Refresh access status to get download URL
        await checkAccessStatus();
      }
    } catch (error) {
      console.error('Error verifying checkout session:', error);
      setError('Failed to verify payment. Please contact support if you have already paid.');
    } finally {
      setLoading(false);
    }
  };
  
  const verifyPaymentIntent = async (paymentIntentId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Verify payment intent and grant access
      const response = await axios.post(`${API_URL}/data/anuga/payment-success`, {
        payment_intent_id: paymentIntentId,
        user_id: 'current_user'
      });
      
      if (response.data.success) {
        setPaymentStatus('success');
        setHasAccess(true);
        // Refresh access status to get download URL
        await checkAccessStatus();
      }
    } catch (error) {
      console.error('Error verifying payment intent:', error);
      setError('Failed to verify payment. Please contact support if you have already paid.');
    } finally {
      setLoading(false);
    }
  };
  

  const checkAccessStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/data/anuga/access`);
      setHasAccess(response.data.hasAccess);
      if (response.data.hasAccess) {
        setDownloadUrl(response.data.downloadUrl);
        setPaymentStatus('success');
      }
    } catch (error) {
      console.error('Error checking access status:', error);
      setError('Failed to check access status');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!downloadUrl) {
      setError('No download URL available');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(downloadUrl, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'anuga_dataset.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      setError('Failed to download dataset');
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactError(null);

    try {
      const response = await axios.post(`${API_URL}/data/anuga/contact`, {
        name: contactForm.name,
        email: contactForm.email,
        company: contactForm.company,
        dataSource: contactForm.dataSource,
        requirements: contactForm.requirements
      });
      
      if (response.data.success) {
        setContactSuccess(true);
        setContactForm({
          name: '',
          email: '',
          company: '',
          dataSource: '',
          requirements: ''
        });
      } else {
        setContactError('Failed to send message. Please try again or contact us directly.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setContactError('Failed to send message. Please try again or contact us directly.');
    } finally {
      setContactLoading(false);
    }
  };

  const handleContactFormChange = (field) => (event) => {
    setContactForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const fetchDatasetsInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/data/anuga/datasets-info`);
      setDatasetsInfo(response.data);
    } catch (error) {
      console.error('Error fetching datasets info:', error);
    }
  };

  const fetchPreviewData = async (datasetType = selectedDataset) => {
    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const response = await axios.get(`${API_URL}/data/anuga/preview?dataset_type=${datasetType}&limit=20`);
      setPreviewData(response.data);
      setShowPreview(true);
    } catch (error) {
      console.error('Error fetching preview data:', error);
      setPreviewError('Failed to load data preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewPageChange = (event, newPage) => {
    setPreviewPage(newPage);
  };

  const handlePreviewRowsPerPageChange = (event) => {
    setPreviewRowsPerPage(parseInt(event.target.value, 10));
    setPreviewPage(0);
  };

  const handleDatasetChange = (event) => {
    const newDataset = event.target.value;
    setSelectedDataset(newDataset);
    setPreviewData(null); // Clear current preview data
    setShowPreview(false); // Hide preview
    setPreviewPage(0); // Reset pagination
  };

  const datasetFeatures = [
    {
      icon: <Description />,
      title: "Excel Format",
      description: "Ready-to-use Excel files that can be directly imported into CRM systems"
    },
    {
      icon: <Security />,
      title: "Complete Contact Info",
      description: "Company addresses, phone numbers, emails, and business profiles"
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
          Anuga Trade Fair Dataset
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 3 }}>
          Access comprehensive trade fair data and market insights from Anuga
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Dataset Information */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Description />
              Dataset Overview
            </Typography>
            <Typography variant="body1" paragraph>
              The Anuga Trade Fair Dataset provides comprehensive data from one of the world's leading 
              food and beverage trade fairs. This dataset contains detailed information about exhibitors 
              and their products, including contact details, company profiles, and product specifications 
              from the actual Anuga trade fair.
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              What's Included:
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="Complete Anuga Exhibitors Dataset - Company details, contact information, and business profiles" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="Complete Anuga Products Dataset - Product listings, categories, and specifications" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="Company profiles with addresses, phone numbers, and email contacts" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="Product categories and detailed product information" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="Including product PDF catalogues and images of products" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                <ListItemText primary="Ready-to-use Excel format for immediate analysis" />
              </ListItem>
            </List>
          </Paper>

          {/* Features Grid */}
          <Grid container spacing={2}>
            {datasetFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {feature.icon}
                      <Typography variant="h6" sx={{ ml: 1 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Data Preview Section */}
          <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableChart />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Dataset Preview
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={showPreview ? <ExpandLess /> : <ExpandMore />}
                onClick={() => {
                  if (!showPreview && !previewData) {
                    fetchPreviewData(selectedDataset);
                  } else {
                    setShowPreview(!showPreview);
                  }
                }}
                disabled={previewLoading}
              >
                {previewLoading ? 'Loading...' : showPreview ? 'Hide Preview' : 'Show Preview'}
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Preview the actual data structure and sample records from the Anuga datasets
            </Typography>

            {/* Dataset Selection */}
            {datasetsInfo && (
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth size="small" sx={{ maxWidth: 300 }}>
                  <InputLabel>Select Dataset</InputLabel>
                  <Select
                    value={selectedDataset}
                    label="Select Dataset"
                    onChange={handleDatasetChange}
                  >
                    {datasetsInfo.datasets.map((dataset) => (
                      <MenuItem 
                        key={dataset.type} 
                        value={dataset.type}
                        disabled={!dataset.available}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>{dataset.name}</Typography>
                          {dataset.available ? (
                            <Chip label={`${dataset.total_rows.toLocaleString()} records`} size="small" color="success" />
                          ) : (
                            <Chip label="Not available" size="small" color="error" />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {datasetsInfo.datasets.find(d => d.type === selectedDataset) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {datasetsInfo.datasets.find(d => d.type === selectedDataset).description}
                  </Typography>
                )}
              </Box>
            )}

            <Collapse in={showPreview}>
              {previewError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {previewError}
                </Alert>
              )}

              {previewData && (
                <Box>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Chip label={`${previewData.dataset_name}`} color="primary" variant="outlined" />
                    <Chip label={`${previewData.total_rows.toLocaleString()} Total Records`} color="primary" variant="outlined" />
                    <Chip label={`${previewData.total_columns} Columns`} color="secondary" variant="outlined" />
                    <Chip label="Excel Format" color="success" variant="outlined" />
                  </Box>

                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {previewData.columns_info.slice(0, 8).map((column) => (
                            <TableCell key={column.name} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                              {column.name}
                            </TableCell>
                          ))}
                          {previewData.columns_info.length > 8 && (
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                              ...
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.preview_data
                          .slice(previewPage * previewRowsPerPage, previewPage * previewRowsPerPage + previewRowsPerPage)
                          .map((row, index) => (
                          <TableRow key={index} hover>
                            {previewData.columns_info.slice(0, 8).map((column) => (
                              <TableCell key={column.name} sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {row[column.name] || '-'}
                              </TableCell>
                            ))}
                            {previewData.columns_info.length > 8 && (
                              <TableCell>...</TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={previewData.preview_data.length}
                    page={previewPage}
                    onPageChange={handlePreviewPageChange}
                    rowsPerPage={previewRowsPerPage}
                    onRowsPerPageChange={handlePreviewRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 20]}
                  />

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                    Showing {Math.min(previewRowsPerPage, previewData.preview_data.length)} of {previewData.preview_data.length} preview records. 
                    Full dataset contains {previewData.total_rows.toLocaleString()} records.
                  </Typography>
                </Box>
              )}
            </Collapse>
          </Paper>
        </Grid>

        {/* Payment Section */}
        <Grid item xs={12} md={4}>
          <Card elevation={3} sx={{ position: 'sticky', top: 20 }}>
            <CardContent>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h4" component="div" color="primary" sx={{ fontWeight: 'bold' }}>
                  €299
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One-time purchase
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {hasAccess ? (
                <Box sx={{ textAlign: 'center' }}>
                  <CheckCircle color="success" sx={{ fontSize: 48, mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Payment Successful!
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                    <Typography variant="body2">
                      The dataset will be sent to your email address within 24 hours.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      If there is any problem, contact us at{' '}
                      <Typography component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        support@forloop.ai
                      </Typography>
                    </Typography>
                  </Alert>
                  <Typography variant="body2" color="text.secondary">
                    You have lifetime access to this dataset
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                    <Typography variant="body2">
                      The access to the dataset will be sent to your email address within 24 hours after the payment.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      If there would be any problem, contact us at{' '}
                      <Typography component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        support@forloop.ai
                      </Typography>
                    </Typography>
                  </Alert>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<CreditCard />}
                    fullWidth
                    sx={{ mb: 2 }}
                    href="https://buy.stripe.com/3cI4gz9c56Yqe6e1MugA802"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Purchase Dataset - €299
                  </Button>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Secure payment powered by Stripe
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Extraction Services Section */}
      <Box sx={{ mt: 8, py: 6, backgroundColor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ fontWeight: 'bold', mb: 2 }}>
            Need Other Data Sources?
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
            We specialize in custom data extraction and continuous monitoring services
          </Typography>

          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <CardContent>
                  <Business sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Custom Data Extraction
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Need data from specific websites, APIs, or databases? We can extract and structure data from virtually any source.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <CardContent>
                  <TrendingUp sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Continuous Monitoring
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Real-time data feeds and monitoring services to keep your data fresh and up-to-date automatically.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
                <CardContent>
                  <Public sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Data Watchdogs
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Automated monitoring systems that alert you to changes, new data, or anomalies in your data sources.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Contact Form */}
          <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold', mb: 3 }}>
              Contact Us for Custom Data Solutions
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
              Tell us about your data needs and we'll provide a customized solution
            </Typography>

            <form onSubmit={handleContactSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Your Name"
                    value={contactForm.name}
                    onChange={handleContactFormChange('name')}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={contactForm.email}
                    onChange={handleContactFormChange('email')}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={contactForm.company}
                    onChange={handleContactFormChange('company')}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Data Source/Website URL"
                    placeholder="e.g., https://example.com or specific database"
                    value={contactForm.dataSource}
                    onChange={handleContactFormChange('dataSource')}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Requirements & Details"
                    multiline
                    rows={4}
                    placeholder="Describe what data you need, frequency of updates, format requirements, etc."
                    value={contactForm.requirements}
                    onChange={handleContactFormChange('requirements')}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<Send />}
                      disabled={contactLoading}
                      sx={{ px: 4 }}
                    >
                      {contactLoading ? <CircularProgress size={24} /> : 'Send Request'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>

            {contactError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {contactError}
              </Alert>
            )}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Or contact us directly at{' '}
                <Typography component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  support@forloop.ai
                </Typography>
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={contactSuccess}
        autoHideDuration={6000}
        onClose={() => setContactSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setContactSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Thank you! We'll get back to you within 24 hours with a custom solution.
        </Alert>
      </Snackbar>
    </Container>
  );
};


export default AnugaDataView;
